// SimulationOverlay: pure canvas renderer for the fire grid.
// All simulation logic lives in src/simulation/fireEngine.js; App.js owns the
// engine and passes render snapshots down. This component just draws:
//   1. terrain / fire / infrastructure cells (with hillshading)
//   2. forecast risk overlay (Monte Carlo burn probability)
//   3. tool animations
//   4. wind vectors

import React, { useEffect, useRef } from "react";
import {
  STATE_EMPTY,
  STATE_TREE,
  STATE_BURNING,
  STATE_WATER,
  STATE_INFRASTRUCTURE,
  STATE_SMOLDERING,
  STATE_INFRASTRUCTURE_BURNT,
  STATE_RETARDANT,
  STATE_DIGGING,
  GRID_SIZE_PX,
} from "../simulation/constants";
import { TOOL_EFFECT_RADIUS } from "../simulation/fireEngine";

const HALF = GRID_SIZE_PX / 2;

const COLOR_WATER = "rgba(65, 105, 225, 0.9)";
const COLOR_BURNING = "rgba(255, 0, 0, 0.9)";
const COLOR_SMOLDERING = "rgba(170, 80, 0, 0.8)";
const COLOR_BURNT_LAND = "rgba(45, 35, 30, 0.9)";
const COLOR_INFRASTRUCTURE_BURNT = "rgba(139, 0, 0, 0.8)";
const COLOR_RETARDANT = "rgba(255, 105, 180, 0.6)";
const COLOR_DIGGING = "rgba(139, 69, 19, 0.7)";
const BORDER_COLOR_RETARDANT_AFTER = "rgba(180, 50, 120, 0.8)";
const BORDER_COLOR_DIGGING_AFTER = "rgba(80, 30, 0, 0.8)";
const STATION_COLOR_INACTIVE = "rgba(0, 180, 255, 0.9)";
const STATION_COLOR_ACTIVE = "rgba(255, 255, 0, 1.0)";

const WIND_VECTOR_SPACING = 10;
const WIND_VECTOR_COLOR = "rgba(255, 255, 0, 0.7)";
const MAX_WIND_SPEED_FOR_VECTOR_LENGTH = 50;
const MAX_WIND_VECTOR_LENGTH_FACTOR = 2.5;
const FLASH_INTERVAL_MS = 500;

const greenForElevation = (elev255, shade) => {
  const g = Math.round((50 + (elev255 / 255) * 155) * shade);
  const r = Math.round(20 * shade);
  return `rgba(${r}, ${Math.min(255, g)}, ${r}, 0.9)`;
};
const greyForElevation = (elev255, shade) => {
  const v = Math.round((100 + (elev255 / 255) * 120) * shade);
  return `rgba(${v}, ${v}, ${v}, 0.92)`;
};

const SimulationOverlay = ({
  gridDimensions,
  renderData, // { grid, markers } snapshot from the engine
  baseTypeGrid, // initial cell types (terrain baseline) from the scenario
  renderElevation, // 0-255 normalized elevation
  shade, // hillshade factors
  droneStations,
  activeMissions,
  droneModeActive,
  weather, // { speed, direction (FROM deg), ... }
  forecast, // damage forecast result or null
  forecastVisible,
  toolEvents, // [{ key, r, c, tool, startTime, duration }]
  zIndexValue,
}) => {
  const baseCanvasRef = useRef(null);
  const dynamicCanvasRef = useRef(null);
  const forecastCanvasRef = useRef(null);
  const animationCanvasRef = useRef(null);
  const windCanvasRef = useRef(null);
  const flashRef = useRef(true);

  const rows = gridDimensions?.rows || 0;
  const cols = gridDimensions?.cols || 0;
  const width = cols * GRID_SIZE_PX;
  const height = rows * GRID_SIZE_PX;

  // --- Base terrain layer: drawn once per scenario -----------------------
  // Static water/vegetation/infrastructure cells are baked into this canvas;
  // the per-tick dynamic layer only draws cells that differ from it.
  useEffect(() => {
    const canvas = baseCanvasRef.current;
    if (!canvas || !rows || !cols || !baseTypeGrid) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const t = baseTypeGrid[r][c];
        const elev = renderElevation?.[r]?.[c] ?? 128;
        const sh = shade?.[r]?.[c] ?? 1;
        let fill;
        if (t === STATE_WATER) fill = COLOR_WATER;
        else if (t === STATE_INFRASTRUCTURE) fill = greyForElevation(elev, sh);
        else fill = greenForElevation(elev, sh);
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.arc(c * GRID_SIZE_PX + HALF, r * GRID_SIZE_PX + HALF, HALF, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }, [baseTypeGrid, rows, cols, width, height, renderElevation, shade]);

  // --- Dynamic layer: only cells whose state differs from the baseline ---
  useEffect(() => {
    const canvas = dynamicCanvasRef.current;
    if (!canvas || !rows || !cols || !renderData?.grid) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    const { grid, markers } = renderData;
    const stationSet = new Set(
      droneModeActive
        ? (droneStations || []).map((s) => `${s.row},${s.col}`)
        : []
    );
    const activeStationSet = new Set(
      (activeMissions || [])
        .filter((m) => m.status === "deploying" && m.origin)
        .map((m) => `${m.origin.row},${m.origin.col}`)
    );

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let r = 0; r < rows; r++) {
      const gridRow = grid[r];
      const baseRow = baseTypeGrid?.[r];
      for (let c = 0; c < cols; c++) {
        const s = gridRow[c];
        const cellKey = `${r},${c}`;
        const isStation = stationSet.has(cellKey);
        // Skip unchanged cells — the base layer already shows them
        if (!isStation && baseRow && s === baseRow[c]) continue;

        let fill;
        if (isStation) {
          fill =
            activeStationSet.has(cellKey) && flashRef.current
              ? STATION_COLOR_ACTIVE
              : STATION_COLOR_INACTIVE;
        } else if (s === STATE_WATER) fill = COLOR_WATER;
        else if (s === STATE_BURNING) fill = COLOR_BURNING;
        else if (s === STATE_SMOLDERING) fill = COLOR_SMOLDERING;
        else if (s === STATE_RETARDANT) fill = COLOR_RETARDANT;
        else if (s === STATE_DIGGING) fill = COLOR_DIGGING;
        else if (s === STATE_EMPTY) fill = COLOR_BURNT_LAND;
        else if (s === STATE_INFRASTRUCTURE_BURNT)
          fill = COLOR_INFRASTRUCTURE_BURNT;
        else if (s === STATE_INFRASTRUCTURE)
          fill = greyForElevation(renderElevation?.[r]?.[c] ?? 128, shade?.[r]?.[c] ?? 1);
        else if (s === STATE_TREE)
          fill = greenForElevation(renderElevation?.[r]?.[c] ?? 128, shade?.[r]?.[c] ?? 1);
        else continue;

        const cx = c * GRID_SIZE_PX + HALF;
        const cy = r * GRID_SIZE_PX + HALF;
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.arc(cx, cy, HALF, 0, 2 * Math.PI);
        ctx.fill();

        if (isStation) continue;

        // Active effect patterns / persistent markers
        const markerType = markers?.[cellKey];
        if (s === STATE_DIGGING) {
          ctx.strokeStyle = "rgba(80, 30, 0, 0.7)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cx - HALF, cy);
          ctx.lineTo(cx + HALF, cy);
          ctx.moveTo(cx, cy - HALF);
          ctx.lineTo(cx, cy + HALF);
          ctx.stroke();
        } else if (s === STATE_RETARDANT) {
          ctx.strokeStyle = "rgba(180, 50, 120, 0.7)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          for (let o = -GRID_SIZE_PX; o <= GRID_SIZE_PX; o += 4) {
            ctx.moveTo(cx - HALF + o, cy - HALF);
            ctx.lineTo(cx + HALF + o, cy + HALF);
          }
          ctx.stroke();
        } else if (
          markerType &&
          (s === STATE_SMOLDERING ||
            s === STATE_INFRASTRUCTURE_BURNT ||
            s === STATE_EMPTY)
        ) {
          ctx.lineWidth = 2;
          if (markerType === "RETARDANT_DROP") {
            ctx.strokeStyle = BORDER_COLOR_RETARDANT_AFTER;
            ctx.beginPath();
            ctx.arc(cx, cy, HALF - 1.5, 0, 2 * Math.PI);
            ctx.stroke();
          } else if (markerType === "DIG_BREAK") {
            ctx.strokeStyle = BORDER_COLOR_DIGGING_AFTER;
            ctx.setLineDash([3, 3]);
            ctx.beginPath();
            ctx.arc(cx, cy, HALF - 1.5, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }
    }
  }, [
    renderData,
    baseTypeGrid,
    rows,
    cols,
    width,
    height,
    renderElevation,
    shade,
    droneStations,
    activeMissions,
    droneModeActive,
  ]);

  // Station flash timer (stations redraw every sim tick, which is 10 Hz
  // while running, so toggling the ref is enough)
  useEffect(() => {
    const id = setInterval(() => {
      flashRef.current = !flashRef.current;
    }, FLASH_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // --- Forecast risk overlay --------------------------------------------
  useEffect(() => {
    const canvas = forecastCanvasRef.current;
    if (!canvas || !rows || !cols) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!forecast || !forecastVisible) return;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const p = forecast.burnProb[r][c];
        if (p < 0.05) continue;
        // yellow -> orange -> red ramp with probability-scaled alpha
        const red = 255;
        const green = Math.round(220 * (1 - p));
        ctx.fillStyle = `rgba(${red}, ${green}, 0, ${0.15 + p * 0.45})`;
        ctx.fillRect(
          c * GRID_SIZE_PX,
          r * GRID_SIZE_PX,
          GRID_SIZE_PX,
          GRID_SIZE_PX
        );
      }
    }
  }, [forecast, forecastVisible, rows, cols, width, height]);

  // --- Tool animations ---------------------------------------------------
  useEffect(() => {
    const canvas = animationCanvasRef.current;
    if (!canvas || !rows || !cols) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    let frameId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const now = Date.now();
      let active = false;
      for (const anim of toolEvents || []) {
        const elapsed = now - anim.startTime;
        if (elapsed >= anim.duration) continue;
        active = true;
        const progress = elapsed / anim.duration;
        const cx = anim.c * GRID_SIZE_PX + HALF;
        const cy = anim.r * GRID_SIZE_PX + HALF;
        const radius = TOOL_EFFECT_RADIUS[anim.tool] || 1;
        ctx.globalAlpha = 1 - progress;
        if (anim.tool === "WATER_DROP" || anim.tool === "RETARDANT_DROP") {
          ctx.strokeStyle =
            anim.tool === "WATER_DROP"
              ? "rgba(100, 150, 255, 0.8)"
              : "rgba(255, 150, 200, 0.8)";
          ctx.lineWidth = 3 * (1 - progress);
          ctx.beginPath();
          ctx.arc(cx, cy, radius * GRID_SIZE_PX * progress, 0, 2 * Math.PI);
          ctx.stroke();
        } else if (anim.tool === "DIG_BREAK") {
          const size = radius * GRID_SIZE_PX * 1.5 * (1 - progress);
          ctx.fillStyle = "rgba(200, 100, 50, 0.7)";
          ctx.fillRect(cx - size / 2, cy - size / 2, size, size);
        } else if (anim.tool === "BACKFIRE") {
          ctx.fillStyle = "rgba(255, 50, 0, 0.6)";
          ctx.beginPath();
          ctx.arc(cx, cy, radius * GRID_SIZE_PX * progress, 0, 2 * Math.PI);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      if (active) frameId = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frameId);
  }, [toolEvents, rows, cols, width, height]);

  // --- Wind vectors ------------------------------------------------------
  useEffect(() => {
    const canvas = windCanvasRef.current;
    if (!canvas || !rows || !cols) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!weather || weather.speed <= 0) return;

    // Arrows point in the direction the wind blows TOWARD
    const towardRad = (((weather.direction ?? 0) + 180) % 360) * (Math.PI / 180);
    const dx = Math.sin(towardRad);
    const dy = -Math.cos(towardRad);
    const speedRatio = Math.min(
      1,
      weather.speed / MAX_WIND_SPEED_FOR_VECTOR_LENGTH
    );
    const len =
      HALF + speedRatio * (GRID_SIZE_PX * MAX_WIND_VECTOR_LENGTH_FACTOR - HALF);

    ctx.strokeStyle = WIND_VECTOR_COLOR;
    ctx.lineWidth = 1.5;
    const half = Math.floor(WIND_VECTOR_SPACING / 2);
    for (let r = half; r < rows; r += WIND_VECTOR_SPACING) {
      for (let c = half; c < cols; c += WIND_VECTOR_SPACING) {
        const sx = c * GRID_SIZE_PX + HALF;
        const sy = r * GRID_SIZE_PX + HALF;
        const ex = sx + len * dx;
        const ey = sy + len * dy;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        const a = Math.atan2(ey - sy, ex - sx);
        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.lineTo(
          ex - 4 * Math.cos(a - Math.PI / 6),
          ey - 4 * Math.sin(a - Math.PI / 6)
        );
        ctx.moveTo(ex, ey);
        ctx.lineTo(
          ex - 4 * Math.cos(a + Math.PI / 6),
          ey - 4 * Math.sin(a + Math.PI / 6)
        );
        ctx.stroke();
      }
    }
  }, [weather, rows, cols, width, height]);

  const layerStyle = (z) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: z,
  });

  return (
    <>
      <canvas
        ref={baseCanvasRef}
        style={layerStyle(zIndexValue)}
        width={width}
        height={height}
      />
      <canvas
        ref={dynamicCanvasRef}
        style={layerStyle(zIndexValue + 1)}
        width={width}
        height={height}
      />
      <canvas
        ref={forecastCanvasRef}
        style={layerStyle(zIndexValue + 2)}
        width={width}
        height={height}
      />
      <canvas
        ref={animationCanvasRef}
        style={layerStyle(zIndexValue + 3)}
        width={width}
        height={height}
      />
      <canvas
        ref={windCanvasRef}
        style={layerStyle(zIndexValue + 4)}
        width={width}
        height={height}
      />
    </>
  );
};

export default SimulationOverlay;
